import { getConstructionDefinition, type ObjectTypeValue } from '@dreamgame/shared';
import { tv } from 'tailwind-variants';
import { selectedConstructionSite } from '../../state/construction';

const constructionDetails = tv({
  slots: {
    root: ['fixed right-4 top-1/2 -translate-y-1/2', 'w-64', 'animate-slide-left', 'z-[1000]'],
    container: [
      'bg-gradient-to-b from-rs-stone to-rs-stone-dark',
      'border-2 border-rs-stone-light',
      'rounded-lg',
      'shadow-rs-panel',
      'overflow-hidden',
    ],
    header: [
      'bg-gradient-to-b from-rs-stone-light to-rs-stone',
      'border-b-2 border-rs-stone-dark',
      'px-4 py-3',
    ],
    title: ['text-rs-gold font-medieval font-bold text-sm tracking-wide text-center uppercase'],
    subtitle: ['text-rs-parchment-dark text-xs text-center mt-1'],
    content: ['p-4 space-y-4'],
    section: ['space-y-2'],
    sectionTitle: ['text-rs-gold text-xs uppercase tracking-wider font-semibold'],
    progressContainer: ['space-y-1'],
    progressBar: [
      'h-4 rounded-sm overflow-hidden',
      'bg-rs-stone-dark',
      'border border-rs-stone-light/30',
    ],
    progressFill: [
      'h-full transition-all duration-300',
      'bg-gradient-to-r from-amber-600 via-amber-500 to-yellow-500',
    ],
    progressText: ['text-xs text-rs-parchment text-center'],
    resourceList: ['space-y-2'],
    resourceRow: ['flex items-center justify-between', 'text-xs'],
    resourceName: ['text-rs-parchment flex items-center gap-2'],
    resourceIcon: ['w-4 h-4 object-contain'],
    resourceAmount: ['font-semibold'],
    resourceComplete: ['text-emerald-400'],
    resourcePending: ['text-rs-parchment-dark'],
    workerInfo: ['flex items-center justify-between', 'text-xs text-rs-parchment'],
    blueprintBadge: [
      'inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-wider',
      'bg-sky-900/50 text-sky-300 border border-sky-600/50',
    ],
  },
});

// Progress bar component
const ProgressBar = ({ progress, label }: { progress: number; label: string }) => {
  const styles = constructionDetails();
  const percentage = Math.round(progress * 100);

  return (
    <div class={styles.progressContainer()}>
      <div class={styles.progressBar()}>
        <div class={styles.progressFill()} style={{ width: `${percentage}%` }} />
      </div>
      <div class={styles.progressText()}>
        {label}: {percentage}%
      </div>
    </div>
  );
};

// Resource requirement row
const ResourceRow = ({
  resourceType,
  required,
  delivered,
}: {
  resourceType: string;
  required: number;
  delivered: number;
}) => {
  const styles = constructionDetails();
  const isComplete = delivered >= required;

  // Format resource type for display (e.g., "wood" -> "Wood")
  const displayName = resourceType.charAt(0).toUpperCase() + resourceType.slice(1);

  return (
    <div class={styles.resourceRow()}>
      <span class={styles.resourceName()}>
        {/* biome-ignore lint/a11y/noNoninteractiveElementInteractions: onError is not interactive */}
        <img
          src={`/items/${resourceType}.png`}
          alt={displayName}
          class={styles.resourceIcon()}
          onError={(e) => {
            // Hide broken images
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
        {displayName}
      </span>
      <span
        class={`${styles.resourceAmount()} ${isComplete ? styles.resourceComplete() : styles.resourcePending()}`}
      >
        {delivered} / {required}
      </span>
    </div>
  );
};

export const ConstructionDetailsPanel = () => {
  const site = selectedConstructionSite.value;
  const styles = constructionDetails();

  if (!site) {
    return null;
  }

  const definition = getConstructionDefinition({
    objectType: site.objectType as ObjectTypeValue,
  });

  if (!definition) {
    return null;
  }

  // Parse resources delivered (stored as JSON or use 0 for now)
  // In a real implementation, the server would track delivered resources per type
  const deliveredResources: Record<string, number> = {};

  return (
    <div class={styles.root()}>
      <div class={styles.container()}>
        {/* Header */}
        <div class={styles.header()}>
          <div class={styles.title()}>{definition.name}</div>
          <div class={styles.subtitle()}>
            <span class={styles.blueprintBadge()}>Blueprint</span>
          </div>
        </div>

        {/* Content */}
        <div class={styles.content()}>
          {/* Progress Section */}
          <div class={styles.section()}>
            <div class={styles.sectionTitle()}>Construction Progress</div>
            <ProgressBar progress={site.progress} label="Building" />
          </div>

          {/* Materials Section */}
          <div class={styles.section()}>
            <div class={styles.sectionTitle()}>Materials Required</div>
            <div class={styles.resourceList()}>
              {definition.buildCost.map((cost) => (
                <ResourceRow
                  key={cost.resourceType}
                  resourceType={cost.resourceType}
                  required={cost.amount}
                  delivered={deliveredResources[cost.resourceType] ?? 0}
                />
              ))}
            </div>
          </div>

          {/* Workers Section */}
          <div class={styles.section()}>
            <div class={styles.sectionTitle()}>Workers</div>
            <div class={styles.workerInfo()}>
              <span>Assigned:</span>
              <span class="font-semibold">{site.workersAssigned}</span>
            </div>
          </div>

          {/* Build Time Info */}
          <div class={styles.section()}>
            <div class={styles.sectionTitle()}>Estimated Time</div>
            <div class={styles.workerInfo()}>
              <span>Base build time:</span>
              <span class="font-semibold">{(definition.buildTime / 20).toFixed(1)}s</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
